import { f0FormField } from '@factorialco/f0-react'
import type { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import {
  FixedAmountInputWrapper,
  MaximumAmountInputWrapper,
  PendingPerWorkedDayDefinition,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/AdditionalCompensationWizardFields'

import {
  type BasicContext,
  DURATION_FIELDS,
  DURATION_FIELD_UNITS,
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_STRATEGY,
  SHOW_PENDING_PER_WORKED_DAY_FIELDS,
  VARIABLE_AMOUNT_STRATEGY,
  durationSecondsToMinutesSchema,
  isNumber,
  isPendingPerWorkedDayDefinitionValues,
  normalizeDefinitionNumber,
  optionalDurationSecondsToMinutesSchema,
} from './additionalCompensationWizard.shared'

const buildPendingPerWorkedDayDefinitionSchema = () =>
  z
    .object({
      amountPerDay: z.number().optional(),
      daysCountedFrom: z.literal('previous_month').optional(),
      minimumHoursPerDay: z.number().optional(),
      workLocations: z.array(z.string()).optional(),
      includedAbsences: z.array(z.string()).optional(),
      eligibleDays: z.array(z.string()).optional(),
    })
    .optional()

const buildDefinitionSchema = ({
  payrollConcepts,
  basicContext,
  selectedPayrollConceptUnitType,
}: {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  basicContext: BasicContext
  selectedPayrollConceptUnitType?: PayrollConceptSelectFieldFragment['unitType']
}) => {
  const pendingPerWorkedDayDefinitionSchema = buildPendingPerWorkedDayDefinitionSchema()

  return z
    .object({
      fixedAmount:
        selectedPayrollConceptUnitType === 'time'
          ? f0FormField(durationSecondsToMinutesSchema, {
              label: i18n.t(
                'contract_versions.additional_compensation.additional_compensation_form_fields.amount_strategy_inputs_fields.fixed_amount_input.label'
              ),
              fieldType: 'duration',
              units: DURATION_FIELD_UNITS,
              size: 'md',
              fields: DURATION_FIELDS,
              renderIf: () => basicContext.amountStrategyType === FIXED_AMOUNT_STRATEGY,
            })
          : f0FormField(z.number().nullable().optional(), {
              label: i18n.t(
                'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.title'
              ),
              fieldType: 'custom',
              placeholder: i18n.t(
                'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.placeholder'
              ),
              renderIf: () => basicContext.amountStrategyType === FIXED_AMOUNT_STRATEGY,
              render: ({
                value,
                onChange,
                error,
              }: {
                value: unknown
                onChange: (val: unknown) => void
                error?: string
              }) => {
                const numericValue = isNumber(value) ? value : undefined

                return (
                  <FixedAmountInputWrapper
                    value={numericValue}
                    onChange={(numberValue) => onChange(numberValue)}
                    onClear={() => onChange(null)}
                    error={error}
                    payrollConcepts={payrollConcepts}
                    selectedPayrollConceptId={basicContext.payrollConceptId}
                    required
                  />
                )
              },
            }),
      maximumAmountPerCycle:
        selectedPayrollConceptUnitType === 'time'
          ? f0FormField(optionalDurationSecondsToMinutesSchema, {
              label: i18n.t(
                'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.maximum_amount_per_cycle.label'
              ),
              fieldType: 'duration',
              units: DURATION_FIELD_UNITS,
              size: 'md',
              fields: DURATION_FIELDS,
              renderIf: () => basicContext.amountStrategyType === VARIABLE_AMOUNT_STRATEGY,
            })
          : f0FormField(z.number().nullable().optional(), {
              label: i18n.t(
                'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.maximum_amount_per_cycle.label'
              ),
              fieldType: 'custom',
              placeholder: i18n.t(
                'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.placeholder'
              ),
              renderIf: () => basicContext.amountStrategyType === VARIABLE_AMOUNT_STRATEGY,
              render: ({
                value,
                onChange,
                error,
              }: {
                value: unknown
                onChange: (val: unknown) => void
                error?: string
              }) => {
                const numericValue = isNumber(value) ? value : undefined

                return (
                  <MaximumAmountInputWrapper
                    value={numericValue}
                    onChange={(numberValue) => onChange(numberValue)}
                    onClear={() => onChange(null)}
                    error={error}
                    payrollConcepts={payrollConcepts}
                    selectedPayrollConceptId={basicContext.payrollConceptId}
                  />
                )
              },
            }),
      pendingPerWorkedDayFields: f0FormField(pendingPerWorkedDayDefinitionSchema, {
        label: '',
        fieldType: 'custom',
        renderIf: () =>
          SHOW_PENDING_PER_WORKED_DAY_FIELDS &&
          basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
        render: ({
          value,
          onChange,
          error,
        }: {
          value: unknown
          onChange: (val: unknown) => void
          error?: string
        }) => {
          const currentValues = isPendingPerWorkedDayDefinitionValues(value) ? value : {}

          return (
            <PendingPerWorkedDayDefinition
              payrollConcepts={payrollConcepts}
              selectedPayrollConceptId={basicContext.payrollConceptId}
              values={currentValues}
              onChange={(nextValues) =>
                onChange({
                  ...currentValues,
                  ...nextValues,
                })
              }
              error={error}
            />
          )
        },
      }),
    })
    .superRefine((data, context) => {
      const isFixedStrategy = basicContext.amountStrategyType === FIXED_AMOUNT_STRATEGY
      const isPerWorkedDayStrategy = basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY
      const fixedAmount = normalizeDefinitionNumber(data.fixedAmount)

      if (isPerWorkedDayStrategy) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t(
            'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.pending_error'
          ),
          path: ['pendingPerWorkedDayFields'],
        })

        return
      }

      if (!isFixedStrategy) return

      if (!fixedAmount || fixedAmount <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('common.required'),
          path: ['fixedAmount'],
        })
      }
    })
}

export { buildDefinitionSchema }
